import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import { Section, ACCENT, formatMXN } from "../cuentas/shared";
import { formatCalendarDate } from "../../utils/dates";
import { resumenInventario, usoDeRecurso } from "../../utils/reportes";
import { tieneEscala, estaVacia } from "../../utils/catalogo";
import { Kpi } from "./shared";
import {
  ArchiveBoxIcon,
  RectangleStackIcon,
} from "@heroicons/react/20/solid";

// Las piezas físicas del venue: cuántas hay, en qué estado, cuánto se usaron
// y qué han costado. El dinero sale del contexto; las piezas se leen una vez
// al abrir (una consulta por servicio con piezas — suelen ser pocos).
const ReporteInventario = ({ rango }) => {
  const navigate = useNavigate();
  const { selectedVenueId, conceptos, costos, events, calendarTimezone: tz } = useVenue();

  const inv = useMemo(
    () => resumenInventario({ conceptos, costos, desde: rango.from, hasta: rango.to, timeZone: tz }),
    [conceptos, costos, rango, tz]
  );

  // El nivel de cada pieza se lee con la escala del propio servicio
  // (FRA-351), así que hace falta el doc completo, no solo el resumen.
  const conceptosPorId = useMemo(() => {
    const map = {};
    (conceptos || []).forEach((c) => {
      if (c?.id) map[c.id] = c;
    });
    return map;
  }, [conceptos]);

  const itemizados = inv.items;
  const idsItemizados = itemizados.map((i) => i.id).join(",");

  // Resumen de piezas físicas por servicio: estado y, si el servicio mide
  // nivel, cuántas están por recargar.
  const [unidadesPorItem, setUnidadesPorItem] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!selectedVenueId || !idsItemizados) {
      setUnidadesPorItem({});
      return;
    }
    Promise.all(
      idsItemizados.split(",").map(async (id) => {
        const item = conceptosPorId[id] || null;
        const conEscala = tieneEscala(item);
        const snap = await getDocs(
          collection(db, "venues", selectedVenueId, "conceptos", id, "unidades")
        );
        const resumen = { total: 0, activas: 0, mantenimiento: 0, baja: 0, conEscala, porRecargar: 0, ok: 0 };
        snap.docs.forEach((d) => {
          const u = d.data();
          resumen.total += 1;
          const estado = u.estado || "activo";
          if (estado === "activo") resumen.activas += 1;
          else if (estado === "mantenimiento") resumen.mantenimiento += 1;
          else resumen.baja += 1;
          if (conEscala) {
            if (estaVacia(item, u.nivel)) resumen.porRecargar += 1;
            else resumen.ok += 1;
          }
        });
        return [id, resumen];
      })
    )
      .then((entries) => {
        if (!cancelled) setUnidadesPorItem(Object.fromEntries(entries));
      })
      .catch((err) => console.error("Piezas del reporte:", err));
    return () => {
      cancelled = true;
    };
  }, [selectedVenueId, idsItemizados, conceptosPorId]);

  if (inv.items.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-10 text-center shadow-sm">
        <ArchiveBoxIcon className="h-8 w-8 mx-auto text-zinc-300" />
        <div className="mt-2 text-zinc-900 dark:text-white font-semibold">
          Ningún servicio del catálogo lleva inventario
        </div>
        <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
          Actívale piezas físicas a
          tus servicios en{" "}
          <button
            type="button"
            onClick={() => navigate("/dashboard/servicios")}
            className="font-medium hover:underline"
            style={{ color: ACCENT }}
          >
            Servicios
          </button>{" "}
          para verlos aquí.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Servicios con piezas"
          value={String(inv.items.length)}
          hint="Los que se dan de alta, se les sigue el estado y se reservan"
        />
        <Kpi
          label="Invertido"
          value={formatMXN(inv.totales.inversion)}
          hint="Lo que costó comprarlos, cuando se capturó"
        />
        <Kpi
          label="Gastado en el período"
          value={formatMXN(inv.totales.gastado)}
          hint="Recargas, refacciones, mantenimiento — sin contar su compra"
        />
      </div>

      {/* Piezas físicas: flota, uso y compromiso — no solo el conteo */}
      {itemizados.length > 0 && (
        <Section icon={RectangleStackIcon} title="Piezas físicas">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 dark:border-zinc-700/40">
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Servicio</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Piezas</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Uso en el período</th>
                  <th className="px-3 py-2 text-left font-semibold text-zinc-600">Comprometidas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {itemizados.map((i) => {
                  const u = unidadesPorItem[i.id];
                  const uso = usoDeRecurso({
                    events,
                    itemId: i.id,
                    desde: rango.from,
                    hasta: rango.to,
                    timeZone: tz,
                  });
                  return (
                    <tr key={i.id}>
                      <td className="px-3 py-2.5 align-top">
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/servicios/${i.id}`)}
                          className="hover:underline font-medium text-left"
                          style={{ color: ACCENT }}
                        >
                          {i.nombre}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {!u ? (
                          <span className="text-zinc-400">…</span>
                        ) : u.total === 0 ? (
                          <span className="text-xs text-zinc-400">Sin piezas dadas de alta</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            <span className="text-zinc-900 dark:text-white font-medium mr-1">
                              {u.total}
                            </span>
                            {u.mantenimiento > 0 && (
                              <Badge color="amber">{u.mantenimiento} en mantenimiento</Badge>
                            )}
                            {u.baja > 0 && <Badge color="zinc">{u.baja} de baja</Badge>}
                            {/* Solo los servicios con escala tienen nivel que
                                vigilar; los demás muestran el conteo. */}
                            {u.conEscala && u.porRecargar > 0 && (
                              <Badge color="red">{u.porRecargar} por recargar</Badge>
                            )}
                            {u.mantenimiento === 0 &&
                              u.baja === 0 &&
                              !(u.conEscala && u.porRecargar > 0) && (
                                <Badge color="emerald">Toda disponible</Badge>
                              )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top text-zinc-600 dark:text-zinc-300">
                        {uso.eventosPeriodo > 0 ? (
                          <>
                            {uso.eventosPeriodo} {uso.eventosPeriodo === 1 ? "evento" : "eventos"}
                            {uso.asignacionesPeriodo > 0 && (
                              <span className="text-zinc-400">
                                {" "}· {uso.asignacionesPeriodo}{" "}
                                {uso.asignacionesPeriodo === 1 ? "pieza asignada" : "piezas asignadas"}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-zinc-400">Sin uso en el período</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {uso.unidadesComprometidas > 0 ? (
                          <span className="text-zinc-900 dark:text-white">
                            {uso.unidadesComprometidas} en {uso.eventosFuturos}{" "}
                            {uso.eventosFuturos === 1 ? "evento próximo" : "eventos próximos"}
                            {/* La disponibilidad es por fecha: lo que importa
                                es el día futuro más cargado, no una resta
                                global contra la flota. */}
                            {u && uso.picoDia && (
                              <span
                                className={`ml-1.5 text-xs ${
                                  uso.picoDia.unidades >= u.activas
                                    ? "text-amber-600 font-medium"
                                    : "text-zinc-400"
                                }`}
                              >
                                · día más cargado:{" "}
                                {formatCalendarDate(uso.picoDia.fecha, tz, { style: "short" })}{" "}
                                con {uso.picoDia.unidades} de {u.activas} en uso
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-zinc-400">Ninguna</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-zinc-400">
            "Comprometidas" son piezas ya asignadas a eventos que aún no
            ocurren. La disponibilidad es por fecha: el dato a vigilar es el
            día más cargado — si ese día usa todas las piezas activas, no
            hay margen para vender otro evento esa fecha.
          </p>
        </Section>
      )}

      <p className="text-xs text-zinc-400">
        Las piezas y lo invertido son una foto de hoy; lo gastado
        corresponde al período elegido. El detalle de cada servicio vive en
        su ficha.
      </p>
    </div>
  );
};

export default ReporteInventario;
